// Safe DOM helpers (WS-23). The ONLY sanctioned way to build HTML from data.
//
// Rule (docs/WORKFLOW.md, "XSS"): text from content.json, resort.json, tour.json, URL
// params, and later the dashboard or booking APIs, reaches the DOM as TEXT. Build markup
// with the `html` tagged template: every interpolated value is escaped unless it is
// already SafeHTML (another html`` result, esc(), or raw() of a constant in our code).
// Arrays are rendered item by item, so `${list.map((x) => html`<li>${x}</li>`)}` works
// without .join(''). ESLint (no-unsanitized) only accepts innerHTML = html`...`.
// URLs from data go through safeUrl() (https/mailto/tel or same-origin only).

class SafeHTML {
  constructor(s) { this.s = s; }
  toString() { return this.s; }
}

const ENT = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/** Escape a value for HTML text or a quoted attribute. SafeHTML passes through unchanged. */
export const esc = (v) => (v instanceof SafeHTML ? v : new SafeHTML(String(v ?? '').replace(/[&<>"']/g, (c) => ENT[c])));

/** Mark a CONSTANT markup string from our own code as safe (icons, fixed fragments). Never pass data. */
export const raw = (s) => new SafeHTML(String(s));

function render(v) {
  if (v == null || v === false || v === true) return '';
  if (Array.isArray(v)) return v.map(render).join('');
  return esc(v).s;
}

/** Tagged template: html`<b>${title}</b>` escapes every interpolation (arrays render item by item). */
export function html(strings, ...values) {
  let out = strings[0];
  for (let i = 0; i < values.length; i++) out += render(values[i]) + strings[i + 1];
  return new SafeHTML(out);
}

/**
 * A URL from data, made safe for href/src. Allows https:, mailto:, tel:, and same-origin URLs
 * (relative paths resolve against `base`: by default the page's <base>, the asset root, as the browser reads a
 * relative src; VIS-195: the page's own address put package photos under /m/<map>/, which is no file). Everything
 * else (javascript:, data:, foreign http:) returns '' so the caller can drop the attribute or element.
 */
export function safeUrl(u, base = globalThis.document?.baseURI || globalThis.location?.href) {
  if (typeof u !== 'string' || !u.trim()) return '';
  let url;
  try { url = new URL(u.trim(), base); } catch { return ''; } // errors:ignore: an unparsable URL from data is simply not linked
  if (url.protocol === 'https:' || url.protocol === 'mailto:' || url.protocol === 'tel:') return url.href;
  if (base && url.origin === new URL(base).origin) return url.href; // same-origin (http on localhost dev)
  return '';
}

/** A CSS colour from config (#rgb, #rrggbb or #rrggbbaa only), else the fallback. */
export const safeColor = (c, fallback) => (typeof c === 'string' && /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(c) ? c : fallback);
